/* eslint-disable no-constant-condition */
/* eslint-disable no-console */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getCategorias, getClientes, getEdificios } from "../../utils";
import {
  aYMD,
  armarCamposCarteleria,
  desdeYMD,
  edificiosOfrecidos,
  finLegadoDe,
  formatoMonto,
  monedasDelPais,
  plazoDesdeFechas,
  resumenEnVivo,
  validarCarteleria,
} from "../../utils/carteleria/formularioCarteleria";
import { normalizarRut } from "../../utils/rut";
import { client } from "../../utils/supabase";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "./stylesCarteleriaForm.scss";

const CarteleriaForm = ({ id, onSuccess, renovarDeId = null }) => {
  // Modo "Renovar": venta NUEVA (sin id) precargada con los parámetros del contrato
  // viejo y vinculada a él por oportunidad_origen_id. Nunca es edición del viejo.
  const esRenovar = !id && !!renovarDeId;
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      tipo_vertical: "",
      referencia_id: "",
      incluye_hardware: false,
      modalidad: "CAPEX",
      monto_hardware: "",
      cuota_hardware_opex: "",
      fee_gestion_unitario: "",
      incluye_contenidos: false,
      fee_contenidos_unitario: "",
      moneda_hardware: "",
      moneda_gestion: "",
      moneda_contenidos: "",
      plazo_contrato_meses: "",
      cantidad_pantallas: "",
      fecha_inicio: "",
      estado: "",
      pais: "",
      // F4 (rediseño de Cartelería): precio mensual o total, forma de facturar, prorrata por días (marcada en altas).
      modo_precio_gestion: "mensual_pantalla",
      forma_facturacion: "mensual",
      facturacion_cada_n_meses: "",
      prorrata_por_dias: true,
    },
  });

  const [clientes, setClientes] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const loadedRefIdRef = useRef(null);
  const [fechaInicio, setFechaInicio] = useState(new Date());
  const [feeGestion, setFeeGestion] = useState("");
  const [montoHardware, setMontoHardware] = useState("");
  const [cuotaOpex, setCuotaOpex] = useState("");
  const [feeContenidos, setFeeContenidos] = useState("");
  const [fechaCierre, setFechaCierre] = useState(null);
  // F4: fin exacto del servicio, precio total del contrato, ventana de contenidos y errores de validación.
  const [fechaFin, setFechaFin] = useState(null);
  const [montoTotalGestion, setMontoTotalGestion] = useState("");
  const [contFechaInicio, setContFechaInicio] = useState(null);
  const [contFechaFin, setContFechaFin] = useState(null);
  const [errs, setErrs] = useState({});

  // Estado para micro-modal de creación rápida de cliente (solo OT)
  const [showCrearClienteModal, setShowCrearClienteModal] = useState(false);
  const [nuevoClienteNombre, setNuevoClienteNombre] = useState("");
  const [nuevoClienteEmpresa, setNuevoClienteEmpresa] = useState("");
  const [nuevoClienteRut, setNuevoClienteRut] = useState("");
  // Categoría obligatoria en toda alta de cliente (decisión de Ignacio, 2026-10-07).
  const [nuevoClienteCategoria, setNuevoClienteCategoria] = useState("0");
  const [categoriasCliente, setCategoriasCliente] = useState([]);
  useEffect(() => {
    getCategorias()
      .then(setCategoriasCliente)
      .catch((err) => console.error("Error cargando categorías:", err));
  }, []);
  const [creandoCliente, setCreandoCliente] = useState(false);

  // Estado para typeahead de referencia (solo OT)
  const [referenciaSearch, setReferenciaSearch] = useState("");
  const [showReferenciaDropdown, setShowReferenciaDropdown] = useState(false);

  const { pais, id: userId } = useAuthContext();

  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : "";

  // 'Cumplido' = estado terminal (contrato cumplido: servicio y hardware entregados, sin
  // obligación remanente). Debe estar en esta lista para que (a) sea seleccionable y
  // (b) editar una fila 'Cumplido' conserve su estado — el reset de fetchNegocio hace
  // `estadoOptions.includes(data.estado) ? data.estado : ""`, así que un valor fuera de
  // la lista se blanquearía al abrir el form.
  const estadoOptions = ["Activo", "100%", "90%", "75%", "50%", "Cumplido"];

  // Monedas ofrecidas por país. NO hay moneda por defecto: cada componente se elige explícitamente (F4).
  const getMonedaOptions = useCallback(() => monedasDelPais(pais), [pais]);

  // Select de moneda obligatorio, sin valor por defecto ("Elegí moneda").
  const renderMoneda = (nombre) => (
    <select {...register(nombre)} className="form-select">
      <option value="">Elegí moneda</option>
      {getMonedaOptions().map((m) => (
        <option key={m} value={m}>
          {m}
        </option>
      ))}
    </select>
  );

  // Normaliza un decimal tipeado a "123,45" (misma regla de siempre en el onBlur).
  const formatearDecimal = (raw) => {
    const clean = (raw || "").replace(/\./g, ",").replace(/[^\d,]/g, "");
    const parts = clean.split(",");
    if (parts.length > 1) {
      const intPart = parts[0].replace(/^0+(?=\d)/, "") || "0";
      return `${intPart},${parts[1].padEnd(2, "0").slice(0, 2)}`;
    }
    if (parts[0]) return `${parts[0].replace(/^0+(?=\d)/, "") || "0"},00`;
    return "";
  };

  const formatosPorMoneda = (porMoneda) =>
    Object.entries(porMoneda || {})
      .map(([moneda, monto]) => formatoMonto(monto, moneda))
      .join(" + ");

  const parseInputNumber = (value) => {
    if (!value || value === "") return 0;
    // Formato chileno: "." es separador de miles, "," es decimal.
    // Strip dots primero, luego coma → punto, parseFloat.
    const num = parseFloat(value.toString().replace(/\./g, "").replace(/,/g, "."));
    return isNaN(num) ? 0 : num;
  };

  // Altas: sin edificios CADUCADO (se conserva el ya elegido en edición/renovación).
  const edificiosVisibles = edificiosOfrecidos(edificios, { esAlta: !id, referenciaActual: loadedRefIdRef.current });

  // Opciones combinadas para typeahead OT (edificios + clientes)
  const referenciaOptions = useMemo(
    () => [
      ...edificiosVisibles.map((e) => ({
        key: `ed-${e.id}`,
        id: e.id,
        label: `[Edificio] ${e.edificio || `ID ${e.id}`}`,
      })),
      ...clientes.map((c) => ({
        key: `cl-${c.id}`,
        id: c.id,
        label: `[Cliente] ${c.nombre || `ID ${c.id}`}`,
      })),
    ],
    [edificiosVisibles, clientes]
  );

  const filteredReferenciaOptions = useMemo(() => {
    if (referenciaSearch.length < 2) return [];
    const q = referenciaSearch.toLowerCase();
    return referenciaOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [referenciaOptions, referenciaSearch]);

  const handleReferenciaSelect = (opt) => {
    setValue("referencia_id", opt.id);
    setReferenciaSearch(opt.label);
    setShowReferenciaDropdown(false);
  };

  const handleReferenciaChange = (e) => {
    const val = e.target.value;
    setReferenciaSearch(val);
    if (val.length === 0) {
      setValue("referencia_id", "");
    }
    setShowReferenciaDropdown(val.length >= 2);
  };

  const handleReferenciaBlur = () => {
    setTimeout(() => setShowReferenciaDropdown(false), 150);
  };

  const handleReferenciaFocus = () => {
    if (referenciaSearch.length >= 2) setShowReferenciaDropdown(true);
  };

  const tipoVertical = watch("tipo_vertical");
  const modoTotal = watch("modo_precio_gestion") === "total_contrato";
  const formaFacturacion = watch("forma_facturacion");
  const incluyeHardware = watch("incluye_hardware");
  const modalidadHardware = watch("modalidad");
  const incluyeContenidos = watch("incluye_contenidos");
  const plazoDerivado = plazoDesdeFechas(aYMD(fechaInicio), aYMD(fechaFin));
  const labelReferencia = tipoVertical === "EO" ? "Edificio" : tipoVertical === "OT" ? "Cliente o Edificio" : "Cliente";

  useEffect(() => {
    fetchClientes();
    fetchEdificios();
    if (id) fetchNegocio(id);
    else if (renovarDeId) precargarRenovacion(renovarDeId);

    setValue("modalidad", "CAPEX"); // CAPEX preseleccionado
    if (normalizedPais) setValue("pais", normalizedPais);
  }, [id, renovarDeId, pais, setValue, normalizedPais]);

  // Re-aplicar referencia_id cuando las listas de opciones estén disponibles
  useEffect(() => {
    if (loadedRefIdRef.current && (edificios.length > 0 || clientes.length > 0)) {
      setValue("referencia_id", loadedRefIdRef.current);
      // Para typeahead OT: poblar el input de búsqueda con el label correspondiente
      const refIdNum = parseInt(loadedRefIdRef.current);
      const ed = edificios.find((e) => parseInt(e.id) === refIdNum);
      const cl = clientes.find((c) => parseInt(c.id) === refIdNum);
      if (ed) setReferenciaSearch(`[Edificio] ${ed.edificio || `ID ${ed.id}`}`);
      else if (cl) setReferenciaSearch(`[Cliente] ${cl.nombre || `ID ${cl.id}`}`);
    }
  }, [edificios, clientes, setValue]);

  useEffect(() => {
    if (!watch("incluye_hardware")) {
      setValue("modalidad", "CAPEX");
      setValue("monto_hardware", "");
      setValue("cuota_hardware_opex", "");
    }
    if (!watch("incluye_contenidos")) {
      setValue("fee_contenidos_unitario", "");
    }
  }, [watch("incluye_hardware"), watch("incluye_contenidos"), setValue]);

  useEffect(() => {
    if (tipoVertical === "OT") {
      setValue("plazo_contrato_meses", null);
      setValue("cantidad_pantallas", null);
      setValue("fee_gestion_unitario", null);
      setValue("incluye_contenidos", false);
      setValue("fee_contenidos_unitario", null);
      setValue("estado", ""); // deja vacío para que el usuario elija
    }
  }, [tipoVertical, setValue]);

  const fetchClientes = async () => {
    try {
      const data = await getClientes({ pais, orderBy: "nombre", ascending: true });
      setClientes(data?.sort((a, b) => a.nombre?.localeCompare(b.nombre) || 0) || []);
    } catch (error) {
      console.error("Error fetching clientes:", error);
      toast.error("Error al cargar clientes");
      setClientes([]);
    }
  };

  const fetchEdificios = async () => {
    try {
      const data = await getEdificios({ pais });
      setEdificios(data?.sort((a, b) => a.edificio?.localeCompare(b.edificio) || 0) || []);
    } catch (error) {
      console.error("Error fetching edificios:", error);
      toast.error("Error al cargar edificios");
      setEdificios([]);
    }
  };

  const abrirCrearClienteModal = () => {
    setNuevoClienteNombre("");
    setNuevoClienteEmpresa("");
    setNuevoClienteRut("");
    setNuevoClienteCategoria("0");
    setShowCrearClienteModal(true);
  };

  const cerrarCrearClienteModal = () => {
    if (creandoCliente) return;
    setShowCrearClienteModal(false);
  };

  const crearClienteRapido = async () => {
    const nombre = nuevoClienteNombre.trim();
    if (!nombre) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!normalizedPais) {
      toast.error("No se ha definido el país del usuario");
      return;
    }
    if (!nuevoClienteCategoria || nuevoClienteCategoria === "0") {
      toast.error("La categoría es obligatoria");
      return;
    }
    const rutNormalizado = normalizarRut(nuevoClienteRut.trim());
    setCreandoCliente(true);
    try {
      // Chequeo de duplicado por RUT (solo si vino RUT). Comparación NORMALIZADA en memoria —
      // los rut guardados pueden tener separadores, así que un `.eq` directo no matchearía.
      if (rutNormalizado) {
        const { data: existentes } = await client
          .from("clientes")
          .select("id, nombre, rut")
          .eq("pais", normalizedPais)
          .not("rut", "is", null);
        const existente = (existentes || []).find((c) => normalizarRut(c.rut) === rutNormalizado);
        if (existente) {
          toast.error(`Ya existe un cliente con ese RUT: ${existente.nombre}`);
          setCreandoCliente(false);
          return;
        }
      }

      // Generar siguiente id (mismo patrón que negocios_carteleria)
      const { data: maxIdData, error: maxIdError } = await client
        .from("clientes")
        .select("id")
        .order("id", { ascending: false })
        .limit(1);

      let nextId = maxIdError || !maxIdData || maxIdData.length === 0 ? 1 : parseInt(maxIdData[0].id) + 1;

      while (true) {
        const { data: checkData, error: checkError } = await client
          .from("clientes")
          .select("id")
          .eq("id", nextId.toString())
          .maybeSingle();
        if (checkError || !checkData) break;
        nextId += 1;
      }

      const payload = {
        id: nextId,
        nombre,
        empresa: nuevoClienteEmpresa.trim() || null,
        rut: rutNormalizado || null,
        pais: normalizedPais,
        categoria: Number(nuevoClienteCategoria),
        eliminada: false,
      };

      const { data: inserted, error } = await client.from("clientes").insert([payload]).select().single();
      if (error) throw error;

      const nuevoCliente = inserted || payload;
      setClientes((prev) => [...prev, nuevoCliente].sort((a, b) => a.nombre?.localeCompare(b.nombre) || 0));
      setValue("referencia_id", nuevoCliente.id);
      setReferenciaSearch(`[Cliente] ${nuevoCliente.nombre}`);
      toast.success("Cliente creado");
      setShowCrearClienteModal(false);
    } catch (err) {
      console.error("Error creando cliente:", err);
      toast.error("Error al crear cliente: " + (err.message || "desconocido"));
    } finally {
      setCreandoCliente(false);
    }
  };

  const fetchNegocio = async (id) => {
    try {
      const { data, error } = await client.from("negocios_carteleria").select("*").eq("id", id).single();
      if (error) throw error;

      setFeeGestion(data.fee_gestion_unitario?.toString().replace(".", ",") || "");
      setMontoTotalGestion(data.monto_total_gestion?.toString().replace(".", ",") || "");
      setMontoHardware(data.monto_hardware?.toString().replace(".", ",") || "");
      setCuotaOpex(data.cuota_hardware_opex?.toString().replace(".", ",") || "");
      setFeeContenidos(data.fee_contenidos_unitario?.toString().replace(".", ",") || "");

      reset({
        ...data,
        plazo_contrato_meses: data.plazo_contrato_meses || "",
        cantidad_pantallas: data.cantidad_pantallas || "",
        pais: data.pais || normalizedPais,
        estado: estadoOptions.includes(data.estado) ? data.estado : "",
        incluye_hardware: data.incluye_hardware || false,
        incluye_contenidos: data.incluye_contenidos || false,
        modalidad: data.modalidad || "CAPEX",
        // Sin moneda por defecto: se muestra lo guardado (vacío = hay que elegirla para guardar).
        moneda_hardware: data.moneda_hardware || "",
        moneda_gestion: data.moneda_gestion || "",
        moneda_contenidos: data.moneda_contenidos || "",
        ejecutivo: data.ejecutivo || "",
        // Eliminamos campos de cuotas
        cuota_hardware_opex: data.modalidad === "OPEX" ? data.cuota_hardware_opex : "",
        modo_precio_gestion: data.modo_precio_gestion || "mensual_pantalla",
        forma_facturacion: data.forma_facturacion || "mensual",
        facturacion_cada_n_meses: data.facturacion_cada_n_meses ?? "",
        // Edición: se conserva el valor guardado (no se toca el cálculo de los proyectos viejos).
        prorrata_por_dias: data.prorrata_por_dias === true,
      });

      // Guardar referencia_id para re-aplicar cuando las listas carguen
      loadedRefIdRef.current = data.referencia_id;

      // Parsear como hora local (no UTC) para evitar desplazamiento de un día
      if (data.fecha_inicio) setFechaInicio(desdeYMD(data.fecha_inicio));
      // Fin del servicio: el guardado, o el fin legado (último día del mes inicio + plazo − 1) si el proyecto es viejo.
      const finGuardado = data.fecha_fin || finLegadoDe(data.fecha_inicio, data.plazo_contrato_meses);
      setFechaFin(finGuardado ? desdeYMD(finGuardado) : null);
      setFechaCierre(data.fecha_cierre ? desdeYMD(data.fecha_cierre) : null);
      setContFechaInicio(data.contenidos_fecha_inicio ? desdeYMD(data.contenidos_fecha_inicio) : null);
      setContFechaFin(data.contenidos_fecha_fin ? desdeYMD(data.contenidos_fecha_fin) : null);
    } catch (error) {
      console.error("Error fetching negocio:", error);
      toast.error("Error al cargar oportunidad");
    }
  };

  // Modo "Renovar": trae el contrato viejo y precarga SOLO sus PARÁMETROS como punto de
  // partida de una venta nueva. NO hereda: id (se genera al guardar), estado (lo elige el
  // usuario), ni el historial de facturación (meses_facturacion / detalle_pagos_hardware /
  // detalle_otros_hardware / fecha_cierre / fecha_inicio) — la renovación factura desde su
  // propia fecha_inicio (hoy por defecto). El vínculo oportunidad_origen_id se setea al guardar.
  const precargarRenovacion = async (origenId) => {
    try {
      const { data, error } = await client.from("negocios_carteleria").select("*").eq("id", origenId).single();
      if (error) throw error;

      // Montos visibles (state de display, con coma decimal como espera el onBlur).
      setFeeGestion(data.fee_gestion_unitario?.toString().replace(".", ",") || "");
      setMontoTotalGestion(data.monto_total_gestion?.toString().replace(".", ",") || "");
      setMontoHardware(data.modalidad === "OPEX" ? "" : data.monto_hardware?.toString().replace(".", ",") || "");
      setCuotaOpex(data.modalidad === "OPEX" ? data.cuota_hardware_opex?.toString().replace(".", ",") || "" : "");
      setFeeContenidos(data.fee_contenidos_unitario?.toString().replace(".", ",") || "");

      // Reset con parámetros explícitos (NO spread de ...data → no arrastra historial).
      reset({
        tipo_vertical: data.tipo_vertical || "",
        referencia_id: data.referencia_id || "",
        incluye_hardware: data.incluye_hardware || false,
        modalidad: data.modalidad || "CAPEX",
        monto_hardware: "",
        cuota_hardware_opex: "",
        fee_gestion_unitario: data.fee_gestion_unitario ?? "",
        incluye_contenidos: data.incluye_contenidos || false,
        fee_contenidos_unitario: data.fee_contenidos_unitario ?? "",
        moneda_hardware: data.moneda_hardware || "",
        moneda_gestion: data.moneda_gestion || "",
        moneda_contenidos: data.moneda_contenidos || "",
        plazo_contrato_meses: "",
        cantidad_pantallas: data.cantidad_pantallas || "",
        modo_precio_gestion: data.modo_precio_gestion || "mensual_pantalla",
        forma_facturacion: data.forma_facturacion || "mensual",
        facturacion_cada_n_meses: data.facturacion_cada_n_meses ?? "",
        prorrata_por_dias: true, // renovación = alta nueva
        nombre_proyecto: data.nombre_proyecto || "",
        ejecutivo: data.ejecutivo || "",
        fecha_inicio: "",
        estado: "", // que el usuario elija (Activo / %)
        pais: data.pais || normalizedPais,
      });

      // referencia_id se re-aplica cuando cargan las listas (mismo patrón que edición).
      loadedRefIdRef.current = data.referencia_id;

      // Fechas de cero: la renovación arranca en su propia fecha_inicio (hoy) y sin cierre.
      setFechaInicio(new Date());
      setFechaFin(null);
      setFechaCierre(null);
      setContFechaInicio(null);
      setContFechaFin(null);
    } catch (error) {
      console.error("Error precargando renovación:", error);
      toast.error("Error al precargar la renovación");
    }
  };

  // Valores normalizados del formulario (RHF + estados de fechas/montos) para validar, resumir y armar el registro.
  const valoresDesdeFormulario = (data) => ({
    ...data,
    id: id || null,
    fecha_inicio: aYMD(fechaInicio),
    fecha_fin: aYMD(fechaFin),
    fecha_cierre: aYMD(fechaCierre),
    contenidos_fecha_inicio: aYMD(contFechaInicio),
    contenidos_fecha_fin: aYMD(contFechaFin),
    fee_gestion_unitario: feeGestion,
    monto_total_gestion: montoTotalGestion,
    fee_contenidos_unitario: feeContenidos,
    monto_hardware: montoHardware,
    cuota_hardware_opex: cuotaOpex,
  });

  const onSubmit = async (data) => {
    if (!normalizedPais) {
      toast.error("No se ha definido el país del usuario");
      return;
    }

    // Validación (monedas explícitas, fechas exactas, montos, forma de facturar) — lógica pura y testeada.
    const valores = valoresDesdeFormulario({ ...data, pais: normalizedPais });
    const errores = validarCarteleria(valores, { esEdicion: !!id });
    setErrs(errores);
    if (Object.keys(errores).length > 0) {
      toast.error(Object.values(errores)[0]);
      return;
    }

    // Solo generar nuevo ID al crear, no al editar
    let nextId = null;
    if (!id) {
      const { data: maxIdData, error: maxIdError } = await client
        .from("negocios_carteleria")
        .select("id")
        .order("id", { ascending: false })
        .limit(1);

      nextId = maxIdError || maxIdData.length === 0 ? 1 : parseInt(maxIdData[0].id) + 1;

      while (true) {
        const { data: checkData, error: checkError } = await client
          .from("negocios_carteleria")
          .select("id")
          .eq("id", nextId.toString())
          .maybeSingle();
        if (checkError || !checkData) break;
        nextId += 1;
      }
    }

    // Todo el registro de plata/fechas sale de UNA función (coherente con los CHECK de la base): modo de precio +
    // fee o monto total, fecha_fin + plazo derivado, forma de facturar, monedas elegidas. Un solo insert/update.
    const submitData = {
      ...(nextId ? { id: nextId.toString() } : {}),
      ...data,
      fecha_inicio: valores.fecha_inicio,
      ...armarCamposCarteleria(valores),
      pais: normalizedPais,
      // Edición: conserva el ejecutivo de la fila. Renovación: conserva el precargado del
      // viejo (fallback al usuario real, 10.104). Alta normal: el usuario que crea.
      ejecutivo: id || esRenovar ? data.ejecutivo || (userId ? String(userId) : null) : userId ? String(userId) : null,
      // Vínculo de renovación (solo en modo renovar). El INSERT del viejo NO se toca.
      ...(esRenovar ? { oportunidad_origen_id: String(renovarDeId) } : {}),
    };

    try {
      const { error } = id
        ? await client.from("negocios_carteleria").update(submitData).eq("id", id).select()
        : await client.from("negocios_carteleria").insert([submitData]).select();

      if (error) throw error;

      toast.success("Oportunidad guardada exitosamente");
      if (onSuccess) onSuccess();

      reset({
        tipo_vertical: "",
        referencia_id: "",
        incluye_hardware: false,
        modalidad: "CAPEX",
        monto_hardware: "",
        cuota_hardware_opex: "",
        fee_gestion_unitario: "",
        incluye_contenidos: false,
        fee_contenidos_unitario: "",
        moneda_hardware: "",
        moneda_gestion: "",
        moneda_contenidos: "",
        plazo_contrato_meses: "",
        cantidad_pantallas: "",
        fecha_inicio: "",
        estado: "100%",
        nombre_proyecto: "",
        pais: normalizedPais,
        modo_precio_gestion: "mensual_pantalla",
        forma_facturacion: "mensual",
        facturacion_cada_n_meses: "",
        prorrata_por_dias: true,
      });

      setFechaInicio(new Date());
      setFechaFin(null);
      setFeeGestion("");
      setMontoTotalGestion("");
      setMontoHardware("");
      setCuotaOpex("");
      setFeeContenidos("");
      setFechaCierre(null);
      setContFechaInicio(null);
      setContFechaFin(null);
      setErrs({});
    } catch (error) {
      console.error("Error saving negocio:", error);
      toast.error("Error al guardar: " + error.message);
    }
  };

  // Resumen en vivo con el MISMO motor que lee el resto de la app (total por componente en su moneda y facturas).
  const resumen = resumenEnVivo(valoresDesdeFormulario({ ...watch(), pais: normalizedPais }));

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="carteleria-form">
        {/* Tipo de Negocio */}
        <div className="mb-3">
          <label className="form-label">Vertical</label>
          <select
            {...register("tipo_vertical", { required: "Selecciona un tipo de vertical" })}
            className="form-select"
            onChange={(e) => {
              setValue("tipo_vertical", e.target.value);
              setValue("referencia_id", ""); // limpia la referencia al cambiar
              setReferenciaSearch("");
              setShowReferenciaDropdown(false);
            }}
          >
            <option value="">Selecciona...</option>
            <option value="EO">Edificios de Oficinas</option>
            <option value="TD">Transformación Digital</option>
            <option value="OT">Otros</option>
          </select>
          {errors.tipo_vertical && <span className="text-danger">{errors.tipo_vertical.message}</span>}
        </div>

        {/* Referencia */}
        <div className="mb-3">
          <label className="form-label">{labelReferencia}</label>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
            {tipoVertical === "OT" ? (
              <>
                {/* Input registrado para que RHF valide referencia_id */}
                <input
                  type="hidden"
                  {...register("referencia_id", {
                    required: "Selecciona una referencia (edificio o cliente)",
                  })}
                />
                <div style={{ flex: 1, position: "relative" }}>
                  <input
                    type="text"
                    className="form-control"
                    value={referenciaSearch}
                    onChange={handleReferenciaChange}
                    onFocus={handleReferenciaFocus}
                    onBlur={handleReferenciaBlur}
                    placeholder="Buscar cliente o edificio (mín. 2 caracteres)..."
                    autoComplete="off"
                  />
                  {showReferenciaDropdown && filteredReferenciaOptions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        maxHeight: "300px",
                        overflowY: "auto",
                        background: "white",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        zIndex: 1090,
                        marginTop: "2px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      {filteredReferenciaOptions.map((opt) => (
                        <button
                          type="button"
                          key={opt.key}
                          className="list-group-item list-group-item-action"
                          onClick={() => handleReferenciaSelect(opt)}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            padding: "8px 12px",
                            border: "none",
                            borderBottom: "1px solid #eee",
                            background: "white",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <select
                {...register("referencia_id", {
                  required: "Selecciona una referencia (edificio o cliente)",
                })}
                className="form-select"
                disabled={!tipoVertical}
                style={{ flex: 1 }}
              >
                <option value="">Selecciona...</option>
                {tipoVertical === "EO" &&
                  edificiosVisibles.map((edificio) => (
                    <option key={edificio.id} value={edificio.id}>
                      [Edificio] {edificio.edificio || `ID ${edificio.id}`}
                    </option>
                  ))}
                {tipoVertical === "TD" &&
                  clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      [Cliente] {cliente.nombre || `ID ${cliente.id}`}
                    </option>
                  ))}
              </select>
            )}
            {tipoVertical === "OT" && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={abrirCrearClienteModal}
                title="Crear nuevo cliente"
                style={{
                  flexShrink: 0,
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  lineHeight: 1,
                }}
              >
                +
              </button>
            )}
          </div>
          {tipoVertical === "OT" && (
            <div className="mb-3">
              <label className="form-label">
                Descripción del proyecto <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                {...register("nombre_proyecto", {
                  required: "La descripción es obligatoria para Otros",
                })}
                className="form-control"
                placeholder="Ej: Venta equipos usados, Reutilización pantallas ex-proyecto, etc."
              />
              {errors.nombre_proyecto && <span className="text-danger">{errors.nombre_proyecto.message}</span>}
            </div>
          )}
          {errors.referencia_id && <span className="text-danger">{errors.referencia_id.message}</span>}
        </div>

        {/* === SERVICIO: pantallas y fechas exactas (solo EO y TD) === */}
        {tipoVertical !== "OT" && (
          <div className="block-section">
            <div style={{ fontWeight: "bold", fontSize: "15px", marginBottom: "8px" }}>Servicio</div>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Cantidad de pantallas</label>
                <input
                  type="text"
                  placeholder="Ej: 3"
                  {...register("cantidad_pantallas", {
                    pattern: { value: /^\d+$/, message: "Solo enteros" },
                  })}
                  className="form-control no-arrows"
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setValue("cantidad_pantallas", val);
                  }}
                />
                {errs.cantidad_pantallas && <span className="text-danger">{errs.cantidad_pantallas}</span>}
              </div>
              <div className="col-md-4">
                <label className="form-label">Inicio del servicio</label>
                <DatePicker
                  selected={fechaInicio}
                  onChange={setFechaInicio}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                />
                {errs.fecha_inicio && <span className="text-danger">{errs.fecha_inicio}</span>}
              </div>
              <div className="col-md-4">
                <label className="form-label">Fin del servicio</label>
                <DatePicker
                  selected={fechaFin}
                  onChange={setFechaFin}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  placeholderText="Seleccionar fecha"
                  minDate={fechaInicio || undefined}
                />
                {errs.fecha_fin && <span className="text-danger">{errs.fecha_fin}</span>}
              </div>
            </div>
            <div className="form-check mb-1">
              <input
                type="checkbox"
                {...register("prorrata_por_dias")}
                id="prorrata_por_dias"
                className="form-check-input"
              />
              <label className="form-check-label" htmlFor="prorrata_por_dias">
                Cobrar el primer y último mes por días
              </label>
            </div>
            <div style={{ fontSize: "13px", color: "#666" }}>
              {plazoDerivado
                ? `Plazo: ${plazoDerivado} ${plazoDerivado === 1 ? "mes" : "meses"} (calculado de las fechas)`
                : "Plazo: —"}
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Estado</label>
          <select
            {...register("estado", {
              required: tipoVertical === "OT" ? "Selecciona el estado de la venta" : "Selecciona estado",
            })}
            className="form-select"
          >
            <option value="">Selecciona...</option>
            {tipoVertical === "OT" ? (
              <>
                <option value="50%">50%</option>
                <option value="75%">75%</option>
                <option value="90%">90%</option>
                <option value="100%">100%</option>
              </>
            ) : (
              estadoOptions.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))
            )}
          </select>
          {errors.estado && <span className="text-danger">{errors.estado.message}</span>}
        </div>

        {/* === GESTIÓN === */}
        {tipoVertical !== "OT" && (
          <div className="block-section">
            <div style={{ fontWeight: "bold", fontSize: "15px", marginBottom: "8px" }}>Gestión</div>
            <div className="row mb-2">
              <div className="col-md-6">
                <label className="form-label">¿Cómo se cobra la gestión?</label>
                <select {...register("modo_precio_gestion")} className="form-select">
                  <option value="mensual_pantalla">Precio mensual por pantalla</option>
                  <option value="total_contrato">Precio total del contrato</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  {modoTotal ? "Precio total del contrato (todas las pantallas)" : "Precio por pantalla por mes"}
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="0,00"
                    value={modoTotal ? montoTotalGestion : feeGestion}
                    className="form-control"
                    onChange={(e) => (modoTotal ? setMontoTotalGestion(e.target.value) : setFeeGestion(e.target.value))}
                    onBlur={() =>
                      modoTotal
                        ? setMontoTotalGestion(formatearDecimal(montoTotalGestion))
                        : setFeeGestion(formatearDecimal(feeGestion))
                    }
                  />
                  {renderMoneda("moneda_gestion")}
                </div>
                {errs.gestion_monto && <span className="text-danger">{errs.gestion_monto}</span>}
                {errs.moneda_gestion && <span className="text-danger">{errs.moneda_gestion}</span>}
              </div>
            </div>
          </div>
        )}

        {/* === FORMA DE FACTURAR === */}
        {tipoVertical !== "OT" && (
          <div className="block-section">
            <div style={{ fontWeight: "bold", fontSize: "15px", marginBottom: "8px" }}>Forma de facturar</div>
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">¿Cada cuánto se factura?</label>
                <select {...register("forma_facturacion")} className="form-select">
                  <option value="mensual">Mensual</option>
                  <option value="cada_n_meses">Cada N meses</option>
                  <option value="unica">Factura única</option>
                </select>
              </div>
              {formaFacturacion === "cada_n_meses" && (
                <div className="col-md-6">
                  <label className="form-label">Cada cuántos meses (N)</label>
                  <input
                    type="text"
                    placeholder="Ej: 4"
                    {...register("facturacion_cada_n_meses")}
                    className="form-control no-arrows"
                    onChange={(e) => setValue("facturacion_cada_n_meses", e.target.value.replace(/\D/g, ""))}
                  />
                  {errs.facturacion_cada_n_meses && (
                    <span className="text-danger">{errs.facturacion_cada_n_meses}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* === HARDWARE === */}
        <div className="block-section">
          <div style={{ marginBottom: "12px" }}>
            <div className="form-check" style={{ margin: 0, display: "flex", alignItems: "center", gap: "6px" }}>
              <input
                type="checkbox"
                {...register("incluye_hardware")}
                id="incluye_hardware"
                className="form-check-input"
                onChange={(e) => {
                  const checked = e.target.checked;
                  setValue("incluye_hardware", checked);
                  setValue("modalidad", "CAPEX");
                  if (!checked) {
                    setValue("monto_hardware", "");
                    setValue("cuota_hardware_opex", "");
                    setFechaCierre(null);
                  }
                }}
              />
              <label
                className="form-check-label"
                htmlFor="incluye_hardware"
                style={{ margin: 0, fontWeight: "bold", fontSize: "15px" }}
              >
                Incluye hardware
              </label>
            </div>
          </div>

          {incluyeHardware && (
            <>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Modalidad del hardware</label>
                  <select
                    {...register("modalidad")}
                    className="form-select"
                    onChange={(e) => {
                      setValue("modalidad", e.target.value);
                      if (e.target.value === "OPEX") setValue("monto_hardware", "");
                      else setValue("cuota_hardware_opex", "");
                    }}
                  >
                    <option value="CAPEX">CAPEX (venta única)</option>
                    <option value="OPEX">OPEX (cuota mensual)</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Moneda del hardware</label>
                  {renderMoneda("moneda_hardware")}
                  {errs.moneda_hardware && <span className="text-danger">{errs.moneda_hardware}</span>}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  {modalidadHardware === "CAPEX" ? (
                    <>
                      <label className="form-label">Monto del hardware (total)</label>
                      <input
                        type="text"
                        placeholder="0"
                        value={montoHardware}
                        className="form-control"
                        onChange={(e) => setMontoHardware(e.target.value)}
                        onBlur={() => {
                          const num = parseInputNumber(montoHardware);
                          if (!num) {
                            setMontoHardware("");
                            return;
                          }
                          const moneda = (watch("moneda_hardware") || "CLP").toUpperCase();
                          const decimals = moneda === "CLP" ? 0 : 2;
                          setMontoHardware(
                            num.toLocaleString("es-CL", {
                              minimumFractionDigits: decimals,
                              maximumFractionDigits: decimals,
                            })
                          );
                        }}
                      />
                      {errs.monto_hardware && <span className="text-danger">{errs.monto_hardware}</span>}
                    </>
                  ) : (
                    <>
                      <label className="form-label">Cuota mensual por pantalla</label>
                      <input
                        type="text"
                        placeholder="0,00"
                        value={cuotaOpex}
                        className="form-control"
                        onChange={(e) => setCuotaOpex(e.target.value)}
                        onBlur={() => setCuotaOpex(formatearDecimal(cuotaOpex))}
                      />
                      {errs.cuota_hardware_opex && <span className="text-danger">{errs.cuota_hardware_opex}</span>}
                    </>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    {modalidadHardware === "CAPEX" ? "Fecha de venta del hardware" : "Fecha de cierre / primera cuota"}
                  </label>
                  <DatePicker
                    selected={fechaCierre}
                    onChange={(date) => setFechaCierre(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control"
                    placeholderText="Seleccionar fecha"
                    isClearable
                  />
                  {errs.fecha_cierre && <span className="text-danger">{errs.fecha_cierre}</span>}
                </div>
              </div>
            </>
          )}
        </div>

        {/* === CONTENIDOS === */}
        {tipoVertical !== "OT" && (
          <div className="block-section">
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <div className="form-check" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  {...register("incluye_contenidos")}
                  id="incluye_contenidos"
                  className="form-check-input"
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setValue("incluye_contenidos", checked);
                    if (!checked) {
                      setFeeContenidos("");
                      setContFechaInicio(null);
                      setContFechaFin(null);
                    }
                  }}
                />
                <label
                  className="form-check-label"
                  htmlFor="incluye_contenidos"
                  style={{ margin: 0, fontWeight: "bold", fontSize: "15px" }}
                >
                  Incluye contenidos
                </label>
              </div>
            </div>
            {incluyeContenidos && (
              <>
                <div className="row mb-3">
                  <div className="col-md-8">
                    <label className="form-label">Precio de contenidos por pantalla por mes (0 = bonificado)</label>
                    <div className="input-group">
                      <input
                        type="text"
                        placeholder="0,00"
                        value={feeContenidos}
                        className="form-control"
                        onChange={(e) => setFeeContenidos(e.target.value)}
                        onBlur={() => setFeeContenidos(formatearDecimal(feeContenidos))}
                      />
                      {renderMoneda("moneda_contenidos")}
                    </div>
                    {errs.moneda_contenidos && <span className="text-danger">{errs.moneda_contenidos}</span>}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Contenidos desde</label>
                    <DatePicker
                      selected={contFechaInicio}
                      onChange={setContFechaInicio}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Sin fecha (desde el inicio)"
                      isClearable
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contenidos hasta</label>
                    <DatePicker
                      selected={contFechaFin}
                      onChange={setContFechaFin}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Sin fecha (hasta el fin)"
                      isClearable
                    />
                    {errs.contenidos_fecha_fin && <span className="text-danger">{errs.contenidos_fecha_fin}</span>}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* === RESUMEN EN VIVO (mismo motor que el resto de la app) === */}
        {resumen && (resumen.totales.length > 0 || resumen.facturas.cantidad > 0) && (
          <div className="block-section" style={{ background: "#f6f8fa" }}>
            <div style={{ fontWeight: "bold", fontSize: "15px", marginBottom: "8px" }}>Resumen del contrato</div>
            {resumen.totales.map((t) => (
              <div key={t.clave}>
                {t.etiqueta}: <strong>{formatoMonto(t.monto, t.moneda)}</strong>
              </div>
            ))}
            <div style={{ marginTop: "6px" }}>
              Facturas: <strong>{resumen.facturas.cantidad}</strong>
              {resumen.facturas.cantidad === 1 && resumen.facturas.lista[0] && (
                <>
                  {" "}
                  de <strong>{formatosPorMoneda(resumen.facturas.lista[0].totalesPorMoneda)}</strong>
                </>
              )}
            </div>
            {resumen.facturas.cantidad > 1 && (
              <div style={{ fontSize: "13px", color: "#555" }}>
                {resumen.facturas.lista.slice(0, 3).map((f, i) => (
                  <div key={`${f.mes}-${f.tipo}-${i}`}>
                    {f.etiqueta || f.mes}: {formatosPorMoneda(f.totalesPorMoneda)}
                  </div>
                ))}
                {resumen.facturas.cantidad > 3 && <div>… y {resumen.facturas.cantidad - 3} más</div>}
              </div>
            )}
          </div>
        )}

        <input type="hidden" {...register("ejecutivo")} />

        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? "Guardando..." : "Guardar"}
        </button>
      </form>

      {showCrearClienteModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1080 }}
          onClick={cerrarCrearClienteModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Crear nuevo cliente</h5>
                <button
                  type="button"
                  className="btn-close"
                  disabled={creandoCliente}
                  onClick={cerrarCrearClienteModal}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Nombre <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={nuevoClienteNombre}
                    onChange={(e) => setNuevoClienteNombre(e.target.value)}
                    disabled={creandoCliente}
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Empresa</label>
                  <input
                    type="text"
                    className="form-control"
                    value={nuevoClienteEmpresa}
                    onChange={(e) => setNuevoClienteEmpresa(e.target.value)}
                    disabled={creandoCliente}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">RUT</label>
                  <input
                    type="text"
                    className="form-control"
                    value={nuevoClienteRut}
                    onChange={(e) => setNuevoClienteRut(e.target.value)}
                    disabled={creandoCliente}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Categoría <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select${nuevoClienteCategoria === "0" ? " is-invalid" : ""}`}
                    value={nuevoClienteCategoria}
                    onChange={(e) => setNuevoClienteCategoria(e.target.value)}
                    disabled={creandoCliente}
                  >
                    <option value="0" disabled>
                      Selecciona una opción
                    </option>
                    {categoriasCliente.map(({ id, nombre }) => (
                      <option key={id} value={id}>
                        {nombre}
                      </option>
                    ))}
                  </select>
                  <div className="invalid-feedback">Este campo es obligatorio</div>
                </div>
                <div className="mb-0">
                  <label className="form-label">País</label>
                  <input type="text" className="form-control" value={normalizedPais} disabled />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={creandoCliente}
                  onClick={cerrarCrearClienteModal}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={creandoCliente || nuevoClienteCategoria === "0"}
                  onClick={crearClienteRapido}
                >
                  {creandoCliente ? "Creando..." : "Crear"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CarteleriaForm;
